'use client';

import { Box, MenuItem, Typography, Pagination } from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { MuiButton } from '@/components/button/MuiButton';
import { ChevronLeft, ChevronRight, MapOutline } from 'mdi-material-ui';

import { useLogic } from './local';
import { RHFSelect } from '@/components/select/RHFSelect';
import { STATION_SORTS } from '@/types/types';
import {
  ibow2_proto_RequestUser,
  ibow2_proto_RequestUserFavorite,
} from '@/init/swagger';
import { Loading } from '@/components/loader/Loading';
import { PublicHospitalMap } from './PublicHospitalMap';
import { PaginationInfo } from '@/helpers/searchHospitalListingsHelper';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import { HospitalMap } from './HospitalMap';
import { Dispatch, SetStateAction } from 'react';

type Props = {
  /**
   * API経由で取得した検索住所
   */
  targetAddress: string;
  isShowMap: boolean;
  toggleIsShowMap: () => void;
  hospitalListings: HospitalListingOutput[];
  children: React.ReactNode;
  requestUser?: ibow2_proto_RequestUser;
  isPublicSearch: boolean;
  paginationInfo: PaginationInfo;
  gotoPageForPagination: (page: number) => void;
  title?: string;
  localFavorites: Array<ibow2_proto_RequestUserFavorite>;
  setLocalFavorites: Dispatch<
    SetStateAction<ibow2_proto_RequestUserFavorite[]>
  >;
  mapMarkerPortalOfficeId?: number | null;
  onMarkerClick?: (listingId: number) => void;
};

export function HospitalSearchPanel({
  targetAddress,
  isShowMap,
  toggleIsShowMap,
  hospitalListings,
  children,
  requestUser,
  isPublicSearch,
  paginationInfo,
  gotoPageForPagination,
  title,
  localFavorites,
  setLocalFavorites,
  mapMarkerPortalOfficeId,
  onMarkerClick,
}: Props) {
  const { control, handleChangeSorts, isPending } = useLogic({
    isPublicSearch,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        px: {
          sm: 5,
          lg: 15,
        },
        pb: 10,
        gap: 3,
        '@media (max-width: 800px)': {
          px: isShowMap ? 0 : 2,
        },
      }}
    >
      <Loading isLoading={isPending} />
      <Box
        sx={{
          width: isShowMap ? '50%' : '100%',
          mt: 3,
          mb: {
            xs: isPublicSearch ? 0 : 10,
            md: isPublicSearch ? 0 : 3,
          },
          '@media (max-width: 800px)': {
            display: isShowMap ? 'none' : '',
            flexDirection: 'column',
            width: isShowMap ? 0 : '100%',
            height: isShowMap ? 0 : '100%',
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <LocationOnOutlinedIcon sx={{ color: '#FFD600' }} />
          <Typography
            variant="JP/Body/m/Regular/Normal"
            sx={{ color: '#595960' }}
          >
            {targetAddress}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
            gap: 1,
            mb: 3,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              flexBasis: 'max-content',
            }}
          >
            <Typography
              variant="JP/Heading/lg/Bold/Tight"
              sx={{ color: '13131D' }}
            >
              {title || '指定した住所周辺の医療機関'}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
            }}
          >
            <Box>
              <RHFSelect
                name={control.register('sort').name}
                control={control}
                placeholder="選択してください"
                variant="outlined"
                onChange={(e) => {
                  e.preventDefault();
                  handleChangeSorts(e.target.value);
                }}
                sx={{
                  m: 0,
                  maxHeight: 32,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#13131D',
                  },
                }}
              >
                {STATION_SORTS.map((sst) => {
                  return (
                    <MenuItem key={sst.value} value={sst.value}>
                      {sst.text}
                    </MenuItem>
                  );
                })}
              </RHFSelect>
            </Box>
            <Box>
              <MuiButton
                startIcon={<MapOutline />}
                onClick={toggleIsShowMap}
                variant="outlined"
                color={isShowMap ? 'primary' : 'inherit'}
                sx={{
                  m: 0,
                  maxHeight: 32,
                }}
              >
                Map表示
              </MuiButton>
            </Box>
          </Box>

          <Box>
            <Typography variant="JP/Body/m/Regular/Normal" align="right">
              {!paginationInfo.isShowPagination &&
                paginationInfo.lastPage !== 0 &&
                hospitalListings && (
                  <>
                    {`全 ${paginationInfo.allListingTotalCount} 件( ${paginationInfo.current.from}〜${paginationInfo.current.to} 件を表示)`}
                  </>
                )}
            </Typography>
          </Box>
        </Box>

        {paginationInfo.isShowPagination && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                sm: 'row',
                xs: 'column',
              },
              flexWrap: 'wrap',
              gap: 1,
              marginBottom: 3,
            }}
          >
            <Box>
              <Typography variant="JP/Body/m/Regular/Normal">
                {`${paginationInfo.current.from}〜${paginationInfo.current.to} 件を表示`}
              </Typography>

              <Typography variant="JP/Body/m/Regular/Normal">/</Typography>

              <Typography variant="JP/Body/m/Regular/Normal">
                {`全 ${paginationInfo.allListingTotalCount} 件`}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <MuiButton
                startIcon={<ChevronLeft />}
                onClick={() =>
                  gotoPageForPagination(paginationInfo.current.page - 1)
                }
                variant="outlined"
                sx={{
                  m: 0,
                  p: 1,
                  minWidth: 'auto',
                  maxHeight: 32,

                  ' & .MuiButton-startIcon': {
                    m: 0,
                  },
                }}
                disabled={
                  paginationInfo.current.page === 1 ||
                  paginationInfo.pages.length <= 1
                }
                aria-label="前のページ"
              />

              <RHFSelect
                name={control.register('page').name}
                control={control}
                onChange={(e) => {
                  e.preventDefault();
                  if (Number(e.target.value)) {
                    gotoPageForPagination(Number(e.target.value));
                  }
                }}
                placeholder="ページ"
                variant="outlined"
                sx={{
                  m: 0,
                  width: 'auto',
                  maxHeight: 32,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#13131D',
                  },
                }}
              >
                {paginationInfo.pages.map((page) => {
                  return (
                    <MenuItem key={page} value={page}>
                      {page}/{paginationInfo.lastPage}
                    </MenuItem>
                  );
                })}
              </RHFSelect>

              <MuiButton
                startIcon={<ChevronRight />}
                onClick={() =>
                  gotoPageForPagination(paginationInfo.current.page + 1)
                }
                variant="outlined"
                sx={{
                  m: 0,
                  p: 1,
                  minWidth: 'auto',
                  maxHeight: 32,

                  ' & .MuiButton-startIcon': {
                    m: 0,
                  },
                }}
                disabled={
                  paginationInfo.current.page === paginationInfo.pages.length
                }
                aria-label="次のページ"
              />
            </Box>
          </Box>
        )}

        <Box
          sx={
            isShowMap
              ? {
                  width: '100%',
                  height: 800,
                  padding: 0.5,
                  overflow: 'auto',
                }
              : {
                  pb: 3,
                }
          }
        >
          {children}
          <Box
            sx={{
              display: paginationInfo.isShowPagination ? 'flex' : 'none',
            }}
          >
            <Pagination
              count={paginationInfo.lastPage}
              page={paginationInfo.current.page}
              siblingCount={3}
              boundaryCount={1}
              onChange={(_, page) => gotoPageForPagination(page)}
            />
          </Box>
        </Box>
      </Box>
      {isShowMap && (
        <Box
          sx={{
            width: isShowMap ? '50%' : '100%',
            '@media (max-width: 800px)': {
              width: isShowMap ? '100%' : 0,
              height: isShowMap ? 'calc(100dvh * 0.62)' : 0,
              marginBottom: isShowMap ? 15 : 0,
            },
          }}
        >
          {!isPublicSearch && requestUser && (
            <HospitalMap
              requestUser={requestUser}
              favorites={localFavorites}
              setFavorites={setLocalFavorites}
              listings={hospitalListings}
              onToggleIsShowMap={toggleIsShowMap}
              mapMarkerPortalOfficeId={mapMarkerPortalOfficeId}
              onMarkerClick={onMarkerClick}
            />
          )}
          {isPublicSearch && (
            <PublicHospitalMap
              listings={hospitalListings}
              onToggleIsShowMap={toggleIsShowMap}
              mapMarkerPortalOfficeId={mapMarkerPortalOfficeId}
              onMarkerClick={onMarkerClick}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
